import { useState } from 'react';

import {
  useNdaDocket,
  useNdaIntakeDecisions,
  useReverseIntakeDecision,
} from '../../hooks/useNda';
import type { NdaIntakeDecision } from '../../types';

/**
 * NDA Intake Decisions (lifecycle rework PR-8d) — the operator surface for the
 * drain's CONFIDENT auto-binds (a detected NDA bound as a revision on an existing
 * matter, or suppressed as a known document). Lets an operator run the intake
 * matcher LIVE and CORRECT a mistake instead of the SQL-only shadow grind.
 *
 * "Correct" un-binds (event-sourced: the reverted turn's document also leaves the
 * matcher baseline, so the wrong matter stops re-matching) and re-routes the
 * source: hold (→ Decision Queue) / re-bind to the right matter / create a new
 * matter / mark noise. Re-binding to an opp-anchored matter can teach the
 * codename→matter alias so the next document auto-routes right.
 *
 * Inline styles mirror NdaDecisionQueue (no CSS-module coupling).
 */

const box: React.CSSProperties = {
  border: '1px solid var(--border, #d9d4c9)',
  borderRadius: 8,
  margin: '8px 0',
  background: 'var(--surface, #fff)',
};

export function NdaIntakeDecisions() {
  const [open, setOpen] = useState(false);
  const decisions = useNdaIntakeDecisions();
  const rows = (decisions.data?.decisions ?? []).filter((d) => !d.reverted_turn);

  // Hide the section entirely until there is at least one live decision — no empty
  // flash, and nothing shown when the drain matcher is dark (no binds exist).
  if (!decisions.isSuccess || rows.length === 0) return null;

  return (
    <div style={box}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            background: '#0f766e',
            color: '#fff',
            borderRadius: 12,
            padding: '1px 9px',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {rows.length}
        </span>
        <span style={{ fontWeight: 600 }}>Intake decisions</span>
        <span style={{ color: '#888', fontSize: 13 }}>
          auto-bound NDAs — review &amp; correct any mis-binds
        </span>
        <span style={{ marginLeft: 'auto', color: '#888' }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{ padding: '4px 14px 12px' }}>
          {rows.map((d) => (
            <DecisionCard key={d.spine_id} decision={d} />
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  bind: 'bound as a revision',
  known_document: 'suppressed (already on matter)',
  attach: 're-bound',
};

function DecisionCard({ decision: d }: { decision: NdaIntakeDecision }) {
  const [correcting, setCorrecting] = useState(false);
  return (
    <div
      style={{
        border: '1px solid var(--border, #e5e1d8)',
        borderRadius: 6,
        padding: '8px 10px',
        margin: '6px 0',
        display: 'grid',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{d.subject || 'detected NDA'}</strong>
        <span style={{ fontSize: 12, color: '#888' }}>
          {KIND_LABEL[d.kind || ''] || d.kind} via {d.via || 'document'}
        </span>
      </div>
      <div style={{ fontSize: 13, color: '#555' }}>
        {d.from_email && <span>from {d.from_email} · </span>}
        matched to <strong>{d.deal_name || `Review #${d.review_id}`}</strong>
      </div>
      {!correcting ? (
        <div style={{ marginTop: 4 }}>
          <button type="button" onClick={() => setCorrecting(true)}>
            ⚑ Correct…
          </button>
        </div>
      ) : (
        <CorrectModal decision={d} onClose={() => setCorrecting(false)} />
      )}
    </div>
  );
}

type Dest = 'attach' | 'create_new' | 'hold' | 'noise';

function CorrectModal({ decision: d, onClose }: { decision: NdaIntakeDecision; onClose: () => void }) {
  const [dest, setDest] = useState<Dest>('attach');
  const [target, setTarget] = useState<number | ''>('');
  // Opt-IN: writing a persistent codename→matter alias affects the LIVE resolver's
  // future routing, so the operator must deliberately choose it (default off).
  const [learnAlias, setLearnAlias] = useState(false);
  const reverse = useReverseIntakeDecision();
  const docket = useNdaDocket();

  // Open matters to re-bind to — exclude the one it's currently (wrongly) bound to.
  const matters = (docket.data?.rows ?? []).filter(
    (r) => r.status === 'open' && r.id !== d.review_id,
  );

  const canSubmit = dest !== 'attach' || target !== '';

  const submit = () => {
    reverse.mutate(
      {
        spineId: d.spine_id,
        destination: dest,
        target_review_id: dest === 'attach' ? Number(target) : undefined,
        learn_alias: dest === 'attach' ? learnAlias : false,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      style={{
        border: '1px dashed var(--border, #cbd5e1)',
        borderRadius: 6,
        padding: '8px 10px',
        marginTop: 6,
        display: 'grid',
        gap: 6,
        background: 'var(--surface-2, #f8f7f3)',
      }}
    >
      <div style={{ fontSize: 13, color: '#555' }}>
        Un-bind from <strong>{d.deal_name || `Review #${d.review_id}`}</strong> and:
      </div>
      <label style={{ fontSize: 13 }}>
        <input type="radio" checked={dest === 'attach'} onChange={() => setDest('attach')} />{' '}
        Re-bind to the correct matter
      </label>
      {dest === 'attach' && (
        <div style={{ margin: '0 0 4px 22px', display: 'grid', gap: 4 }}>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Choose a matter…</option>
            {matters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.deal_name || `Review #${m.id}`}
              </option>
            ))}
          </select>
          <label style={{ fontSize: 12, color: '#555' }}>
            <input
              type="checkbox"
              checked={learnAlias}
              onChange={(e) => setLearnAlias(e.target.checked)}
            />{' '}
            Remember this codename so future documents auto-route here
          </label>
        </div>
      )}
      <label style={{ fontSize: 13 }}>
        <input type="radio" checked={dest === 'create_new'} onChange={() => setDest('create_new')} />{' '}
        Create a new matter (it&apos;s a new NDA)
      </label>
      <label style={{ fontSize: 13 }}>
        <input type="radio" checked={dest === 'hold'} onChange={() => setDest('hold')} />{' '}
        Send to the Decision Queue to resolve later
      </label>
      <label style={{ fontSize: 13 }}>
        <input type="radio" checked={dest === 'noise'} onChange={() => setDest('noise')} />{' '}
        Mark as noise (not a real NDA)
      </label>

      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="button" disabled={!canSubmit || reverse.isPending} onClick={submit}>
          {reverse.isPending ? 'Working…' : 'Apply correction'}
        </button>
        <button type="button" disabled={reverse.isPending} onClick={onClose}>
          Cancel
        </button>
      </div>
      {reverse.isError && (
        <div style={{ color: '#b91c1c', fontSize: 12 }}>
          Correction failed — it may have been resolved already, or the NDA-review budget is
          paused. Refresh and retry.
        </div>
      )}
    </div>
  );
}
